import { z } from "zod";
import { publicConfigSchema } from "@shared/business-schema.ts";

const num = z.coerce.number();

export const serviceSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  category: z.string(),
  duration_minutes: z.number(),
  price_amount: num,
  price_is_from: z.boolean(),
  price_on_request: z.boolean().default(false),
  image_path: z.string().nullable(),
  multi_day: z.boolean(),
});
export type Service = z.infer<typeof serviceSchema>;

export const mediaSchema = z.object({
  id: z.uuid(),
  kind: z.enum(["cover", "gallery", "service", "logo"]),
  path: z.string(),
  alt: z.string(),
  service_id: z.uuid().nullable(),
  origin: z.enum(["config", "owner"]),
});
export type Media = z.infer<typeof mediaSchema>;

export const studioSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  status: z.enum(["preview", "live", "suspended"]),
  name: z.string(),
  timezone: z.string(),
  currency: z.string(),
  locale: z.string(),
  config: publicConfigSchema.partial({ review_notes: true }),
  config_version: z.number(),
  rules: z.object({
    slot_step_minutes: z.number(),
    lead_time_minutes: z.number(),
    horizon_days: z.number(),
    cancel_cutoff_minutes: z.number(),
  }),
  ai_enabled: z.boolean(),
  services: z.array(serviceSchema),
  hours: z.array(z.object({ weekday: z.number(), opens: z.string(), closes: z.string() })),
  media: z.array(mediaSchema),
});
export type Studio = z.infer<typeof studioSchema>;

export const slotSchema = z.object({ starts_at: z.string(), local_date: z.string(), local_time: z.string() });
export type Slot = z.infer<typeof slotSchema>;

export const bookingSchema = z.object({
  id: z.uuid(),
  status: z.enum(["confirmed", "completed", "cancelled", "no_show"]),
  starts_at: z.string(),
  ends_at: z.string(),
  service_name: z.string(),
  duration_minutes: z.number(),
  price_amount: num,
  currency: z.string(),
  resource_name: z.string(),
  customer_name: z.string(),
  comment: z.string(),
  version: z.number(),
  is_demo: z.boolean(),
  can_change: z.boolean(),
  service_id: z.uuid(),
  tenant: z.object({ slug: z.string(), name: z.string(), timezone: z.string(), address: z.string().nullable() }),
});
export type PublicBooking = z.infer<typeof bookingSchema>;

export const createBookingResultSchema = z.object({
  booking_id: z.uuid(),
  access_token: z.string().regex(/^bk_[A-Za-z0-9_-]{43}$/),
  booking: bookingSchema,
  replayed: z.boolean(),
});

/* ---------- owner ---------- */

export const ownerStudioSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(["preview", "live", "suspended"]),
  role: z.string(),
  timezone: z.string(),
  currency: z.string(),
  accent: z.string().nullable(),
});
export type OwnerStudio = z.infer<typeof ownerStudioSchema>;

export const agendaBookingSchema = z.object({
  id: z.uuid(),
  status: z.enum(["confirmed", "completed", "cancelled", "no_show"]),
  starts_at: z.string(),
  ends_at: z.string(),
  occupied_until: z.string(),
  service_id: z.uuid(),
  service_name: z.string(),
  resource_id: z.uuid(),
  resource_name: z.string(),
  price_amount: num,
  currency: z.string(),
  comment: z.string(),
  source: z.string(),
  is_demo: z.boolean(),
  version: z.number(),
  customer: z.object({ name: z.string(), phone: z.string(), email: z.string().nullable() }),
  paid_amount: num,
});
export type AgendaBooking = z.infer<typeof agendaBookingSchema>;

export const agendaSchema = z.object({
  timezone: z.string(),
  bookings: z.array(agendaBookingSchema),
  blocks: z.array(z.object({ id: z.uuid(), resource_id: z.uuid(), resource_name: z.string(), starts_at: z.string(), ends_at: z.string(), reason: z.string() })),
  resources: z.array(z.object({ id: z.uuid(), name: z.string(), kind: z.string(), is_active: z.boolean() })),
  services: z.array(z.object({ id: z.uuid(), name: z.string(), duration_minutes: z.number(), price_amount: num, is_active: z.boolean() })),
});
export type Agenda = z.infer<typeof agendaSchema>;

export const statsSchema = z.object({
  period: z.object({ preset: z.string(), from: z.string(), to: z.string(), timezone: z.string() }),
  currency: z.string(),
  visits: z.number(),
  completed_orders: z.object({ count: z.number(), value: num }),
  payments_received: z.object({ count: z.number(), amount: num }),
  upcoming: z.object({ count: z.number(), expected_value: num }),
  cancelled: z.number(),
  no_show: z.number(),
  top_services: z.array(z.object({ service_name: z.string(), count: z.number() })),
  includes_demo: z.boolean(),
});
export type Stats = z.infer<typeof statsSchema>;

export const readinessSchema = z.object({
  status: z.enum(["preview", "live", "suspended"]),
  problems: z.array(z.string()),
  demo_bookings: z.number().optional(),
});
